import { useGaiaWebsocket } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentStore, useChatStore, useSessionId } from "@/sdk";
import { useTypedMostRecentTask } from "@/sdk/agent/hooks/useMostRecentTask";
import { usePartner } from "@/sdk/agent/hooks/usePartner";
import { useProductOfferStore } from "../../stores";
import type { CommunicationType } from "../types";
import { type SendNotificationResponse, sendNotification } from "./api/sendNotification";

const logger = getLogger({ module: "HandleSendLink" });

export const useHandleSendLink = () => {
    const sessionId = useSessionId();
    const agentId = useAgentStore((state) => state.agentId);
    const websocketObj = useGaiaWebsocket();
    const partner = usePartner();
    const { isRealTimeComplianceCheckEnabled } = usePartnerFeatureConfigured();

    const { isVoice, isChat, chatTask } = useTypedMostRecentTask();
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const activeChatId = useChatStore((state) => state.activeChatId);

    if (primaryProducts == null) {
        throw new Error("Expected data is undefined");
    }

    if (!isVoice && !isChat) {
        return () => Promise.reject(new Error("Send link is only available for voice or chat tasks"));
    }

    const sendComplianceEvent = () => {
        if (!isVoice || !isRealTimeComplianceCheckEnabled || !websocketObj || websocketObj.loading) {
            return;
        }

        const gaiaPayload = {
            action: "orchestrator-invoke",
            appName: "soluto-sales-compliance-cue",
            messageType: "Notification",
            correlationId: crypto.randomUUID(),
            messageId: crypto.randomUUID(),
            message: "",
            sessionId: activeChatId ?? sessionId,
            sessionGroupId: sessionId,
            userId: agentId,
            userType: "Expert",
            partner,
        };

        try {
            websocketObj.sendJsonMessage(gaiaPayload);
            logger.info("Successfully sent compliance validation event to GAIA");
        } catch (error) {
            logger.error({ error }, "Failed to send compliance validation event to GAIA");
        }
    };

    return async (commType: CommunicationType, contactToSend: string): Promise<SendNotificationResponse> => {
        // Fire GAIA notification in parallel (fire-and-forget)
        sendComplianceEvent();

        // Send the actual notification
        return sendNotification({
            sessionId,
            partner,
            productOfferId: selectedProduct?.id ?? primaryProducts[0].id,
            productSku: selectedProduct?.productSku ?? primaryProducts[0].productSku,
            customerEmailId: commType === "EMAIL" && contactToSend ? contactToSend : undefined,
            customerMdn: commType === "SMS" && contactToSend ? contactToSend : undefined,
            customerChatId: activeChatId,
            workerSid: agentId,
            topic: isChat ? chatTask?.topic : undefined,
            language: isChat ? chatTask?.language : undefined,
        });
    };
};
